/**
 * One production company's approved season, as a workbook.
 *
 * What this is for: invoicing and the argument that follows it. A production
 * company that queries an amount is handed the whole season — every criterion,
 * every ruling with the name of whoever made it, every payment line and the
 * entire comment thread — in a file they can open, sort and send to their own
 * accountant. Until now the same answer meant an admin reading forty detail
 * screens out loud.
 *
 * **Approved only, and deliberately so.** Anything earlier is still being
 * decided, and putting a provisional amount into a spreadsheet is how a
 * provisional amount becomes a number somebody quotes back at you. The season
 * overview is where open work is looked at; this file is the record of what
 * was agreed.
 *
 * **Nothing is recomputed.** An approved game carries its `reward` frozen at
 * the moment the producer accepted it, rates and all, so the payment sheet is
 * a transcription rather than an arithmetic. `computeReward` is not imported
 * here on purpose: a later rate card must not restate an agreed invoice, and
 * the only way to be sure of that is not to have the formula in the room. The
 * one exception is a game approved before the reward was stored at all, which
 * is marked as such rather than silently recalculated.
 *
 * This file is pure: no Firestore, no ExcelJS, no browser. The service next
 * door gathers the documents and turns the result into a real `.xlsx`, and
 * `scripts/verify-export.ts` pins the shape without needing either.
 */
import {
  CriterionDef,
  EvaluationComment,
  EvaluationDecision,
  EvaluationGame,
  EvaluationSeason,
  GROUP_LABELS,
  GROUP_ORDER,
} from '../../model/evaluation/evaluation';
import {DECLARATION_LABELS, declarationLabel, declarationState} from '../declaration';
import {effectiveValue, maximumCut} from '../reward';

export type CellValue = string | number | Date | null;

export type CellFormat = 'date' | 'datetime' | 'money' | 'percent' | 'text';

export interface ExportColumn {
  header: string;
  width: number;
  format?: CellFormat;
}

export interface ExportSheet {
  name: string;
  /** Label/value pairs printed above the table; only the first sheet has them. */
  meta?: [string, CellValue][];
  columns: ExportColumn[];
  rows: CellValue[][];
  /** A totals line under the table, bold. Same width as `columns`. */
  footer?: CellValue[];
}

export interface ExportWorkbook {
  fileName: string;
  sheets: ExportSheet[];
}

/** Who asked for the file and when — it ends up in the header block. */
export interface ExportContext {
  by: string;
  at: Date;
  /**
   * The clock the timestamps are written on. Excel has no notion of a time
   * zone, so every instant in the file is rendered as the exporter's local
   * wall clock and this says which one that was — otherwise "approved at
   * 14:12" is two hours of argument waiting to happen.
   */
  timeZone?: string;
}

/** One game with the two subcollections the export needs. */
export interface ExportGameData {
  game: EvaluationGame;
  decisions: EvaluationDecision[];
  comments: EvaluationComment[];
}

/**
 * The games this export covers, oldest first.
 *
 * Selected by `channelId`, never by `producerName`: the name is what a
 * production company is called this month and the channel number is what it
 * is. A company renamed mid-season has two names on its games and one channel,
 * and keying on the name would quietly export half the invoice.
 */
export function approvedGames(games: EvaluationGame[], channelId: number): EvaluationGame[] {
  return games
    .filter(game => game.channelId === channelId && game.state === 'approved')
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
}

/**
 * What to call the production in the file, given its games.
 *
 * The most recent name wins, for the renaming case above: an invoice goes out
 * under the name the company has now, not the one it had in March.
 */
export function producerName(games: EvaluationGame[], fallback = ''): string {
  return [...games].reverse().find(game => !!game.producerName)?.producerName ?? fallback;
}

// --- the sheets ------------------------------------------------------------

const SUMMARY_COLUMNS: ExportColumn[] = [
  {header: 'Date', width: 12, format: 'date'},
  {header: 'Match no', width: 10},
  {header: 'Broadcast', width: 38},
  {header: 'Home', width: 20},
  {header: 'Away', width: 20},
  {header: 'Competition', width: 18},
  {header: 'Venue', width: 20},
  {header: 'Video id', width: 11},
  {header: 'ČT Sport+', width: 10},
  {header: 'Recording (min)', width: 14},
  {header: 'Game on screen', width: 14, format: 'percent'},
  {header: 'Operated camera', width: 18},
  {header: 'Camera status', width: 15},
  {header: 'Declared by', width: 22},
  {header: 'Confirmed by', width: 22},
  {header: 'Dispute note', width: 40, format: 'text'},
  {header: 'Approved by', width: 22},
  {header: 'Approved at', width: 18, format: 'datetime'},
  {header: 'Amount', width: 12, format: 'money'},
  {header: 'Currency', width: 9},
  {header: 'Amount agreed at', width: 18, format: 'datetime'},
  {header: 'Comments', width: 10},
  {header: 'Open comments', width: 14},
  {header: 'StayLive dashboard', width: 34, format: 'text'},
];

const PAYMENT_COLUMNS: ExportColumn[] = [
  {header: 'Date', width: 12, format: 'date'},
  {header: 'Broadcast', width: 38},
  {header: 'Video id', width: 11},
  {header: 'Line', width: 18},
  {header: 'What it is', width: 34},
  {header: 'Detail', width: 44, format: 'text'},
  {header: 'Amount', width: 12, format: 'money'},
];

const CRITERIA_COLUMNS: ExportColumn[] = [
  {header: 'Date', width: 12, format: 'date'},
  {header: 'Broadcast', width: 38},
  {header: 'Video id', width: 11},
  {header: 'Group', width: 20},
  {header: 'Criterion', width: 20},
  {header: 'What it is', width: 34},
  {header: 'Counts as', width: 12},
  {header: 'Result', width: 12},
  {header: 'Value', width: 9},
  {header: 'Production cut', width: 14, format: 'percent'},
  {header: 'Tool measured', width: 13},
  {header: 'Tool note', width: 44, format: 'text'},
  {header: 'Left to a person', width: 15},
  {header: 'Found at', width: 11},
  {header: 'Ruled', width: 9},
  {header: 'Ruled by', width: 22},
  {header: 'Ruled at', width: 18, format: 'datetime'},
  {header: 'Reason', width: 50, format: 'text'},
];

const COMMENT_COLUMNS: ExportColumn[] = [
  {header: 'Date', width: 12, format: 'date'},
  {header: 'Broadcast', width: 38},
  {header: 'Video id', width: 11},
  {header: 'Written at', width: 18, format: 'datetime'},
  {header: 'By', width: 24},
  {header: 'About', width: 34},
  {header: 'Said', width: 80, format: 'text'},
  {header: 'Resolved at', width: 18, format: 'datetime'},
];

/**
 * The operated-camera declaration, in words.
 *
 * `declarationState` returns the ids the screens switch on; this file is read
 * by a production company's accountant, and `toConfirm` means nothing to them.
 */
const CAMERA_STATUS: { [state: string]: string } = {
  undeclared: 'not declared',
  none: 'declared as none',
  toConfirm: 'claimed, not yet confirmed',
  confirmed: 'confirmed',
  disputed: 'disputed',
};

/** What a criterion does to the money, in the contract's own terms. */
const ROLE_LABELS: { [role: string]: string } = {
  gate: 'absolute condition',
  penalty: 'cuts the fee',
  paid: 'paid separately',
  info: 'not paid',
  declared: 'declared',
};

/**
 * How a criterion's number reads in words.
 *
 * A bare 0.6 in a spreadsheet is unarguable in the wrong way — nobody can tell
 * whether it is good. The value stays in its own column for anyone who wants
 * to do arithmetic on it; this one says what it meant.
 */
function resultLabel(value: number | null, def: CriterionDef,
                     season: EvaluationSeason): string {
  if (value === null) {
    return 'not measured';
  }
  if (maximumCut(def.id, season) !== null) {
    return value >= 1 ? 'full marks' : `cut ${Math.round((1 - value) * 100)}%`;
  }
  if (def.role === 'declared') {
    // "declared, 0" says nothing — and 0 is a real answer here, not a failure.
    // The words the producer picked are what belongs in the cell.
    return DECLARATION_LABELS[String(value)] ?? `${value}`;
  }
  if (value >= 1) {
    return 'met';
  }
  if (value <= 0) {
    return 'not met';
  }
  return 'partly';
}

/** Seconds into the recording, as the detail screen writes them. */
function clock(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) {
    return null;
  }
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * A Firestore `Timestamp`, a `Date` or an ISO string, as a `Date`.
 *
 * Duck-typed rather than imported: this file must not pull Firestore in, and
 * `toDate` is the only thing about a Timestamp that matters here. A real date
 * in the cell is what lets Excel sort and filter it as one.
 */
export function asDate(value: any): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      return null;
    }
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function person(name: string | null | undefined, uid: string | null | undefined): string | null {
  return name ?? uid ?? null;
}

function yesNo(value: boolean | null | undefined): string {
  return value ? 'yes' : 'no';
}

/** Criteria in the order the contract lists them, groups included. */
function orderedCriteria(season: EvaluationSeason): CriterionDef[] {
  const criteria = season?.criteria ?? [];
  return [...criteria].sort((a, b) =>
    GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)
    || criteria.indexOf(a) - criteria.indexOf(b));
}

function decisionsByCriterion(decisions: EvaluationDecision[]): { [id: string]: EvaluationDecision } {
  const byCriterion: { [id: string]: EvaluationDecision } = {};
  decisions.forEach(decision => byCriterion[decision.criterion] = decision);
  return byCriterion;
}

// --- the workbook ----------------------------------------------------------

/**
 * Build the whole file.
 *
 * `data` is expected to be exactly what `approvedGames` selected, with each
 * game's two subcollections attached. Anything else in it is still written —
 * the filter belongs to the caller so the subcollection reads can be limited
 * to the games that will be in the file — but `verify-export.ts` pins the pair
 * so the two cannot drift.
 */
export function buildExport(season: EvaluationSeason,
                            channelId: number,
                            data: ExportGameData[],
                            context: ExportContext): ExportWorkbook {
  const games = data.map(entry => entry.game);
  const name = producerName(games, `channel ${channelId}`);
  const currency = season?.currency ?? '';
  const criteria = orderedCriteria(season);

  const summary: CellValue[][] = [];
  const payment: CellValue[][] = [];
  const criteriaRows: CellValue[][] = [];
  const comments: CellValue[][] = [];
  let grandTotal = 0;

  data.forEach(({game, decisions, comments: thread}) => {
    const date = asDate(game.date);
    const byCriterion = decisionsByCriterion(decisions);
    const reward = game.reward;
    const total = reward?.total ?? 0;
    grandTotal += total;

    summary.push([
      date,
      game.matchNo ?? null,
      game.name ?? null,
      game.home ?? null,
      game.away ?? null,
      game.competition ?? null,
      game.venue ?? null,
      game.videoId ?? null,
      yesNo(game.ctSportPlus),
      game.durationS ? Math.round(game.durationS / 60) : null,
      game.facts?.playShare ?? null,
      declarationLabel(game),
      CAMERA_STATUS[declarationState(game)] ?? declarationState(game),
      person(game.declarations?.declaredByName, game.declarations?.declaredBy),
      person(game.declarations?.confirmedByName, game.declarations?.confirmedBy),
      game.declarations?.disputeNote ?? null,
      person(game.approvedByName, game.approvedBy),
      asDate(game.approvedAt),
      total,
      reward?.currency ?? currency,
      // Empty means the game was approved before the amount was stored with
      // it — the one case where this file cannot say what was agreed, which
      // is worth a blank cell rather than a number invented today.
      asDate(reward?.computedAt),
      thread.length || null,
      thread.filter(comment => !comment.resolvedAt).length || null,
      game.dashboardUrl ?? null,
    ]);

    (reward?.lines ?? []).forEach(line => payment.push([
      date,
      game.name ?? null,
      game.videoId ?? null,
      line.id,
      line.label,
      line.detail ?? null,
      line.amount,
    ]));

    criteria.forEach(def => {
      const measured = game.measurements?.[def.id];
      const decision = byCriterion[def.id] ?? null;
      const value = effectiveValue(def.id, game, byCriterion);
      const cut = maximumCut(def.id, season);
      criteriaRows.push([
        date,
        game.name ?? null,
        game.videoId ?? null,
        GROUP_LABELS[def.group] ?? def.group,
        def.id,
        def.label,
        ROLE_LABELS[def.role] ?? def.role,
        resultLabel(value, def, season),
        value,
        cut !== null && value !== null ? (1 - value) * cut : null,
        measured?.value ?? null,
        measured?.note || null,
        measured?.review ? 'yes' : null,
        clock(measured?.at),
        decision ? decision.value : null,
        decision ? person(decision.byName, decision.by) : null,
        decision ? asDate(decision.at) : null,
        decision?.note || null,
      ]);
    });

    thread.forEach(comment => comments.push([
      date,
      game.name ?? null,
      game.videoId ?? null,
      asDate(comment.at),
      person(comment.byName, comment.by),
      // A comment hangs off a criterion or off the broadcast as a whole, and
      // the thread reads very differently depending on which — "the opener"
      // against "this one" is the difference between an objection and a note.
      comment.criterion
        ? criteria.find(def => def.id === comment.criterion)?.label ?? comment.criterion
        : 'the broadcast as a whole',
      comment.text ?? null,
      asDate(comment.resolvedAt),
    ]));
  });

  const meta: [string, CellValue][] = [
    ['Production', name],
    ['StayLive channel', channelId],
    ['Season', season?.season ?? ''],
    ['Approved broadcasts', data.length],
    ['Agreed total', grandTotal],
    ['Currency', currency],
    ['Rate card version', season?.evalVersion ?? ''],
    ['Exported by', context.by],
    ['Exported at', context.at],
    // Excel stores a date and no time zone, so every timestamp below is the
    // exporter's wall clock. Saying which one costs a row and settles the
    // question before somebody asks it.
    ['Times shown in', context.timeZone ?? 'UTC'],
  ];

  /** A totals line: empty across, a label at the front, a number where it belongs. */
  const totalsRow = (columns: ExportColumn[], at: number): CellValue[] => {
    const row: CellValue[] = columns.map(() => null);
    row[0] = 'Total';
    row[at] = grandTotal;
    return row;
  };
  const amountColumn = (columns: ExportColumn[]) =>
    columns.findIndex(column => column.header === 'Amount');

  return {
    fileName: fileName(season?.season ?? '', name),
    sheets: [
      {
        name: 'Summary',
        meta,
        columns: SUMMARY_COLUMNS,
        rows: summary,
        footer: totalsRow(SUMMARY_COLUMNS, amountColumn(SUMMARY_COLUMNS)),
      },
      {
        name: 'Payment',
        columns: PAYMENT_COLUMNS,
        rows: payment,
        footer: totalsRow(PAYMENT_COLUMNS, amountColumn(PAYMENT_COLUMNS)),
      },
      {name: 'Criteria', columns: CRITERIA_COLUMNS, rows: criteriaRows},
      {name: 'Comments', columns: COMMENT_COLUMNS, rows: comments},
    ],
  };
}

/**
 * A filename somebody can find again a year later.
 *
 * Diacritics are stripped rather than kept: the file is mailed to accountants
 * on Windows, and "Sportovní.xlsx" has come back mangled from that trip often
 * enough that the name is not worth the risk. The production's real name is
 * inside the file, on the first sheet.
 */
export function fileName(season: string, name: string): string {
  const slug = (name || 'production')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `evaluation-${season || 'season'}-${slug || 'production'}-approved.xlsx`;
}
